import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {HTMLElement | null} */
  #menuPanel = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache summary element for faster access (INP optimization)
    this.#summary = this.refs.details?.querySelector('summary') || null;
    this.#menuPanel = this.refs.details?.querySelector('[data-header-drawer]') ?? null;
    this.#backdrop = this.refs.details?.querySelector('.menu-drawer__backdrop') ?? null;

    this.addEventListener('keyup', this.#onKeyUp);
    // Defer animated element listeners to avoid blocking initialization (INP optimization)
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => this.#setupAnimatedElementListeners());
    } else {
      setTimeout(() => this.#setupAnimatedElementListeners(), 0);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * Optimized for speed - avoids unnecessary checks
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!event?.target || !(event.target instanceof Element)) return this.refs.details;
    
    // Fast path: if target is already in our details, return it
    if (this.refs.details.contains(event.target)) {
      return event.target.closest('details') || this.refs.details;
    }
    
    return this.refs.details;
  }

  /**
   * Prefer scheduling after the current frame so the pointer handler stays short (INP).
   * Falls back to rAF + timeout when Prioritized Task Scheduling is unavailable.
   * @param {() => void} task
   */
  #deferOpenAfterPaint(task) {
    if (typeof scheduler !== 'undefined' && typeof scheduler.postTask === 'function') {
      scheduler.postTask(task, { priority: 'user-visible' });
      return;
    }
    requestYieldCallback(task);
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent default summary click behavior since we manually control the open state
    if (event) {
      event.preventDefault();
    }
    if (this.isOpen) {
      return this.close(event);
    }
    // Opening: yield before mutating a very large <details> subtree (layout + style).
    this.#deferOpenAfterPaint(() => this.open(event));
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    // Fast path: use cached refs for main drawer (most common case)
    const details = event ? this.#getDetailsElement(event) : this.refs.details;
    const summary = details === this.refs.details && this.#summary 
      ? this.#summary 
      : details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details && details.hasAttribute('open')) {
      return;
    }

    /**
     * INP: keep the click handler tiny — only attribute/class toggles.
     * Avoid forced layout reads (e.g. offsetHeight): this drawer’s DOM is very large.
     * Focus setup runs after the next paint via requestYieldCallback.
     */
    const isFirstOpen = !this._hasOpenedOnce;
    if (isFirstOpen) {
      this._hasOpenedOnce = true;
      details.setAttribute('data-no-animation', 'true');
    }

    details.setAttribute('open', '');
    details.classList.add('menu-open');
    summary.setAttribute('aria-expanded', 'true');

    if (isFirstOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            details.removeAttribute('data-no-animation');
          }, 50);
        });
      });
    }

    const focusRoot = this.#menuPanel ?? details;
    requestYieldCallback(() => trapFocus(focusRoot));
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @param {Event} [event]
   */
  close(event) {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    // Use cached summary for main drawer, query for submenus
    const summary = details === this.refs.details ? (this.#summary || details.querySelector('summary')) : details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const animationRoots =
      details === this.refs.details
        ? /** @type {Element[]} */ (
            [this.#menuPanel, this.#backdrop].filter((n) => n instanceof HTMLElement)
          )
        : null;
    const animationTargets =
      details === this.refs.details && animationRoots?.length
        ? animationRoots
        : details;
    const animationOptions =
      details === this.refs.details && animationRoots?.length ? { subtree: false } : { subtree: true };

    onAnimationEnd(
      animationTargets,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]');
          openDetails.forEach(reset);
        } else {
          const focusRoot = this.#menuPanel ?? this.refs.details;
          requestYieldCallback(() => trapFocus(focusRoot));
        }
      },
      animationOptions
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
